import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Power, PowerOff, Trash2, Box, Bot, RefreshCw, AlertTriangle } from "lucide-react";
import { useBrainImage } from "@/api/queries/brainImages";
import { useAgents } from "@/api/queries/agents";
import { useUpdateBrainImage, useDeleteBrainImage } from "@/api/mutations/brainImages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { AgentMiniTable } from "@/components/AgentMiniTable";
import { relativeTime } from "@/lib/relativeTime";
import { ApiError } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UpdateBrainImageDialog } from "@/components/brain-image-actions/UpdateBrainImageDialog";

export function BrainImageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useBrainImage(id);
  const agentsQuery = useAgents();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const updateMutation = useUpdateBrainImage();
  const deleteMutation = useDeleteBrainImage();

  const errorMessage =
    error instanceof ApiError && error.status === 404 ? `Brain-Image "${id}" nicht gefunden` : (error as Error | undefined)?.message;

  function handleToggleActive() {
    if (!data) return;
    updateMutation.mutate(
      { id: data.id, active: !data.active },
      {
        onSuccess: () => toast.success(data.active ? "Image deaktiviert" : "Image aktiviert"),
        onError: (err) => toast.error(`Toggle fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function handleDelete() {
    if (!data) return;
    setDeleteError(null);
    deleteMutation.mutate(data.id, {
      onSuccess: () => {
        toast.success(`Brain-Image ${data.name} gelöscht`);
        setDeleteOpen(false);
        navigate("/brain-images", { replace: true });
      },
      onError: (err) => {
        const msg = (err as Error).message;
        setDeleteError(msg);
        toast.error(`Delete fehlgeschlagen: ${msg}`);
      },
    });
  }

  // ----- Loading / Error -----
  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Brain-Image…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/brain-images" label="Brain-Images" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />Retry</Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const usedBy = (agentsQuery.data ?? []).filter((a) => a.brainSpec?.imageId === data.id && !a.revokedAt);

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/brain-images"
        backLabel="Brain-Images"
        icon={Box}
        title={data.name}
        badge={
          <Badge variant={data.active ? "active" : "disabled"}>
            {data.active && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {data.active ? "active" : "inactive"}
          </Badge>
        }
        sub={data.description}
        actions={
          <div className="flex flex-wrap gap-2" data-testid="brainimage-actions">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="brainimage-action-edit">
              <Pencil className="h-4 w-4" />Edit
            </Button>
            <Button variant="outline" size="sm" onClick={handleToggleActive} disabled={updateMutation.isPending} data-testid="brainimage-action-toggle">
              {data.active ? <><PowerOff className="h-4 w-4" />Deactivate</> : <><Power className="h-4 w-4" />Activate</>}
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="brainimage-action-delete">
              <Trash2 className="h-4 w-4" />Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <SectionCard
          icon={<Bot className="h-[18px] w-[18px]" />}
          title={`Genutzt von (${usedBy.length})`}
          description="Agents mit diesem Brain-Image."
        >
          <AgentMiniTable agents={usedBy} emptyText="Kein Agent nutzt dieses Image." />
        </SectionCard>

        <PropGrid
          rows={[
            ["ID", <span className="mono">{data.id}</span>],
            ["Image-Ref", <span className="mono break-all">{data.imageRef}</span>],
            ["Digest", data.digest ? <span className="mono text-xs">{data.digest}</span> : <span className="text-text3">—</span>],
            ["Active", <Badge variant={data.active ? "active" : "disabled"}>{data.active ? "active" : "inactive"}</Badge>],
            ["Created By", <span className="mono text-xs">{data.createdBy}</span>],
            ["Erstellt", <span className="text-text2">{relativeTime(data.createdAt)}</span>],
          ]}
        />
      </div>

      {editOpen && <UpdateBrainImageDialog open onOpenChange={setEditOpen} image={data} />}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Brain-Image löschen"
        description="Bestehende Agents mit diesem Image laufen unverändert weiter, können aber das Image nach Restart nicht mehr ziehen wenn auch aus Registry verschwunden."
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        requiredTypeText={data.id}
        onConfirm={handleDelete}
      />
    </div>
  );
}
